import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from 'yup';

//icons
import { colors } from "../../../../../../../theme/theme";

//components
import { toast } from "react-toastify";
import { Badge, Button, Card, Col, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap"
import Error from "../../../../../../components/Error";
import { Modal } from "../../../../../../components/modal";

//services
import { passwordChange, mailChange } from "../../../../../../services/profile.service";
import { MdOutlineVisibility, MdOutlineVisibilityOff } from "react-icons/md";

const validationSchemaMail = Yup.object({
    email: Yup.string().email('E-mail inválido').required("Este campo é obrigatório"),
    confirmEmail: Yup.string().email('E-mail inválido').required("Este campo é obrigatório").oneOf([Yup.ref('email')], 'Os e-mails digitados são diferentes.'),
}).required();

const validationSchemaPassword = Yup.object({
    currentPassword: Yup.string().required("Este campo é obrigatório"),
    newPassword: Yup.string().required("Este campo é obrigatório"),
    confirmPassword: Yup.string().required("Este campo é obrigatório").oneOf([Yup.ref('newPassword')], 'As senhas digitadas são diferentes.'),
}).required();

export const CardAccessDetail = (props: any): JSX.Element => {
    const { user, updateContext, userContext } = props;
    const { t: translation } = useTranslation();

    const [loadingModal, setLoadingModal] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState<boolean>(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
    const [updateEmail, setUpdateEmail] = useState<boolean>(false);
    const [emailChanged, setEmailChanged] = useState("");
    const [updateEmailPending, setUpdateEmailPending] = useState<boolean>(false);
    const [updatePassword, setUpdatePassword] = useState<boolean>(false);

    const { register: registerMail, handleSubmit: handleSubmitMail, formState: { errors: errorsMail } } = useForm({ resolver: yupResolver(validationSchemaMail) });
    const { register: registerPassword, handleSubmit: handleSubmitPassword, formState: { errors: errorsPassword } } = useForm({ resolver: yupResolver(validationSchemaPassword) });

    const onSubmitEmailUpdate = (formEmail: any) => {
        setLoadingModal(true)
        let obj = {
            email_old: user.email,
            email_new1: formEmail.email,
            email_new2: formEmail.confirmEmail,
        }

        mailChange(obj).then(() => {
            setEmailChanged(formEmail.email)
            updateContext({ ...userContext, email: formEmail.email});
            setUpdateEmailPending(true);
            setUpdateEmail(false);
            setLoadingModal(false)
            toast.success(`Um e-mail de confirmação foi enviado ao endereço ${formEmail.email}. Se o novo endereço não for confirmado no prazo de 3 dias a alteração será descartada.`)
        }).catch((err) => {
            setLoadingModal(false)
            toast.error(err.response.data[0]);
        })
    }

    const onSubmitPassword = (formPassword: any) => {
        setLoadingModal(true)
        let obj = {
            password_old: formPassword.currentPassword,
            password_new1: formPassword.newPassword,
            password_new2: formPassword.confirmPassword,
        }

        passwordChange(obj).then(() => {
            setLoadingModal(false)
            toast.success('Senha alterada com sucesso!')
            setUpdatePassword(false);
        }).catch((err) => {
            setLoadingModal(false)
            toast.error(err.response.data[0]);
        })
    }

    return (
        <Card className="account-card mb-4">
            <Card.Body>
                <Card.Title className="ml-5 mr-5 mb-0">
                    <Row>
                        <Col xs={12} className="d-flex align-items-center">
                            <h4 className="mb-0" style={{ fontSize: '22px' }}>{translation('userAccount.access')}</h4>
                        </Col>
                    </Row>
                </Card.Title>
                <hr />
                <Row className="py-3">
                    <Col md={12} className="d-flex align-items-center ml-5 mr-5">
                        <Row className="col-11">
                            <Col xs={12} className="mb-2">
                                <Row>
                                    <Col sm={6}>
                                        <h6>
                                            <span className="text-darker-grey">{translation('userAccount.email')}: </span>
                                            {user.email}
                                            {
                                                updateEmailPending && (
                                                    <OverlayTrigger placement={'top'} overlay={<Tooltip>Um e-mail de confirmação foi enviado ao endereço {emailChanged}. Se o novo endereço não for confirmado no prazo de 3 dias a alteração será descartada.</Tooltip>}>
                                                        <Badge className="badge-mail-pending">{translation('userAccount.pendingMail')}</Badge>
                                                    </OverlayTrigger>
                                                )
                                            }
                                        </h6>
                                    </Col>
                                    <Col sm={6} className="d-flex justify-content-end">
                                        <Button variant="link" style={{ textDecoration: 'none' }} onClick={() => setUpdateEmail(true)}>{translation('userAccount.updateEmail')}</Button>
                                    </Col>
                                </Row>
                            </Col>
                            <Col xs={12} className="mb-2">
                                <Row>
                                    <Col sm={6}>
                                        <h6><span className="text-darker-grey">{translation('userAccount.password')}:</span> ********</h6>
                                    </Col>
                                    <Col sm={6} className="d-flex justify-content-end">
                                        <Button variant="link" style={{ textDecoration: 'none' }} onClick={() => setUpdatePassword(true)}>{translation('userAccount.updatePassword')}</Button>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Card.Body>

            <Modal 
                headerTitle={translation('userAccount.updateEmail')} 
                submitButtonText={translation('save')} 
                show={updateEmail} 
                onHide={() => setUpdateEmail(false)} 
                onSubmit={handleSubmitMail(onSubmitEmailUpdate)} 
                variant="form-modal"
                loading={loadingModal}
            >
                <Form>
                    <Form.Group className="mb-2">
                        <Form.Label className="text-darker-grey">{translation('userAccount.currentEmail')}:</Form.Label>
                        <Form.Control type="text" placeholder={translation('userAccount.currentEmail')} defaultValue={user.email} disabled/>
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Label className="text-darker-grey">{translation('userAccount.newEmail')}:</Form.Label>
                        <Form.Control type="text" placeholder={translation('userAccount.newEmail')} {...registerMail("email", { required: true })} />
                        {errorsMail.email ? (<div className="w-100"> <Error>{errorsMail.email?.message}</Error> </div>) : ""}
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Label className="text-darker-grey">{translation('userAccount.confirmNewEmail')}:</Form.Label>
                        <Form.Control type="text" placeholder={translation('userAccount.confirmNewEmail')} {...registerMail("confirmEmail", { required: true })} />
                        {errorsMail.confirmEmail ? (<div className="w-100"> <Error>{errorsMail.confirmEmail?.message}</Error> </div>) : ""}
                    </Form.Group>
                </Form>
            </Modal>

            <Modal 
                headerTitle={translation('userAccount.updatePassword')} 
                submitButtonText={translation('save')} 
                show={updatePassword} 
                onHide={() => setUpdatePassword(false)} 
                onSubmit={handleSubmitPassword(onSubmitPassword)}
                variant="form-modal"
                loading={loadingModal}
                >
                <Form>
                    <Form.Group className="mb-2">
                        <Form.Label className="text-darker-grey">{translation('userAccount.currentPassword')}:</Form.Label>
                        <Form.Control type={showCurrentPassword ? "text" : "password"} placeholder={translation('userAccount.currentPassword')} {...registerPassword("currentPassword", { required: true })}/>
                        <span className="eye-password-icon-modal">
                            {
                                showCurrentPassword ? (
                                    <MdOutlineVisibility style={{ width: "22px", height: "22px", color: colors.greyscale.dark_grey }} onClick={() => setShowCurrentPassword(false)} />
                                ) : (
                                    <MdOutlineVisibilityOff style={{ width: "22px", height: "22px", color: colors.greyscale.dark_grey }} onClick={() => setShowCurrentPassword(true)} />
                                )
                            }
                        </span>
                        {errorsPassword.currentPassword ? (<div className="w-100"> <Error>{errorsPassword.currentPassword?.message}</Error> </div>) : ""}
                    </Form.Group>

                    <Form.Group className="mb-2">
                        <Form.Label className="text-darker-grey">{translation('userAccount.newPassword')}:</Form.Label>
                        <Form.Control type={showPassword ? "text" : "password"} placeholder={translation('userAccount.newPassword')} {...registerPassword("newPassword", { required: true })} />
                        <span className="eye-password-icon-modal">
                            {
                                showPassword ? (
                                    <MdOutlineVisibility style={{ width: "22px", height: "22px", color: colors.greyscale.dark_grey }} onClick={() => setShowPassword(false)} />
                                ) : (
                                    <MdOutlineVisibilityOff style={{ width: "22px", height: "22px", color: colors.greyscale.dark_grey }} onClick={() => setShowPassword(true)} />
                                )
                            }
                        </span>
                        {errorsPassword.newPassword ? (<div className="w-100"> <Error>{errorsPassword.newPassword?.message}</Error> </div>) : ""}
                    </Form.Group>

                    <Form.Group className="mb-2">
                        <Form.Label className="text-darker-grey">{translation('userAccount.confirmNewPassword')}:</Form.Label>
                        <Form.Control type={showConfirmPassword ? "text" : "password"} placeholder={translation('userAccount.confirmNewPassword')} {...registerPassword("confirmPassword", { required: true })} />
                        <span className="eye-password-icon-modal">
                            {
                                showConfirmPassword ? (
                                    <MdOutlineVisibility style={{ width: "22px", height: "22px", color: colors.greyscale.dark_grey }} onClick={() => setShowConfirmPassword(false)} />
                                ) : (
                                    <MdOutlineVisibilityOff style={{ width: "22px", height: "22px", color: colors.greyscale.dark_grey }} onClick={() => setShowConfirmPassword(true)} />
                                )
                            }
                        </span>
                        {errorsPassword.confirmPassword ? (<div className="w-100"> <Error>{errorsPassword.confirmPassword?.message}</Error> </div>) : ""}
                    </Form.Group>
                </Form>
            </Modal>
        </Card>
    )
}